/**
 * Cookie consent — two categories.
 *
 * Google Analytics (with Microsoft Clarity under the same consent) and Meta
 * Pixel are the App's non-essential cookies — everything else in the cookie
 * policy is needed for the App to run at all.
 * They are different purposes with different recipients (analytics vs.
 * advertising, Google vs. Meta), so they get independent consent rather than
 * one shared toggle: accepting one must not silently accept the other.
 *
 * Both tags live inside the same Google Tag Manager container, configured in
 * Tag Manager itself — this file has no visibility into that configuration.
 * The interface between the two is Google's Consent Mode: before the
 * container ever loads, we push the visitor's choice onto `dataLayer` as
 * `analytics_storage` / `ad_storage` / `ad_user_data` / `ad_personalization`.
 * Whether the Analytics tag and the Pixel tag actually honour that and stay
 * quiet until granted is a Tag Manager setting ("Additional Consent Checks"
 * on each tag) that has to be turned on in the GTM UI — this code can offer
 * the signal, not enforce that the container respects it.
 */
const KEY = "telc.cookieConsent";
const GTM_ID = "GTM-K9X672WH";

export type Category = "analytics" | "marketing";
export type CategoryState = "accepted" | "declined";
export type ConsentState = Record<Category, CategoryState>;

const DEFAULT_STATE: ConsentState = { analytics: "declined", marketing: "declined" };

function isCategoryState(v: unknown): v is CategoryState {
  return v === "accepted" || v === "declined";
}

function read(): ConsentState | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { analytics, marketing } = parsed as Record<string, unknown>;
    if (!isCategoryState(analytics) || !isCategoryState(marketing)) return null;
    return { analytics, marketing };
  } catch {
    return null;
  }
}

let consent = read();
const listeners = new Set<(value: ConsentState | null) => void>();

export const getConsent = (): ConsentState | null => consent;

export function setConsent(next: ConsentState): void {
  const prev = consent;
  consent = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Private browsing or storage disabled — the choice just won't stick.
  }

  pushConsentSignal(next);

  if (next.analytics === "declined") clearCookies((name) => /^_ga(_|$)|^_gac_|^_clck$|^_clsk$|^CLID$/.test(name));
  if (next.marketing === "declined") clearCookies((name) => /^_fbp$|^_fbc$|^fr$/.test(name));

  if (next.analytics === "accepted" || next.marketing === "accepted") loadGTM();

  // Revoking a category that was already granted needs a reload: whatever it
  // loaded keeps running in memory for the rest of this page view — removing
  // a <script> tag or flipping a dataLayer flag does not stop code already
  // executing.
  const revoked =
    (prev?.analytics === "accepted" && next.analytics === "declined") ||
    (prev?.marketing === "accepted" && next.marketing === "declined");
  if (revoked && gtmLoaded) {
    location.reload();
    return;
  }

  for (const listener of listeners) listener(next);
}

export function onConsentChange(listener: (value: ConsentState | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Every call site builds a full `ConsentState`, defaulting the category it
 *  isn't touching to what's on record (or declined, for a first-time visitor). */
export function withCategory(category: Category, state: CategoryState): ConsentState {
  return { ...(consent ?? DEFAULT_STATE), [category]: state };
}

function clearCookies(matches: (name: string) => boolean): void {
  const names = document.cookie
    .split(";")
    .map((pair) => pair.split("=")[0]!.trim())
    .filter(matches);
  for (const name of names) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }
}

function pushConsentSignal(state: ConsentState | null): void {
  const w = window as typeof window & { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  const gtag = (...args: unknown[]): void => {
    w.dataLayer!.push(args);
  };
  gtag("consent", state ? "update" : "default", {
    analytics_storage: state?.analytics === "accepted" ? "granted" : "denied",
    ad_storage: state?.marketing === "accepted" ? "granted" : "denied",
    ad_user_data: state?.marketing === "accepted" ? "granted" : "denied",
    ad_personalization: state?.marketing === "accepted" ? "granted" : "denied",
  });
  if (state?.analytics === "accepted") pushClarityConsent(state);
}

/**
 * Clarity does not read Consent Mode. Since 2025-10-31 a visit from the EEA
 * without its own signal runs cookieless, and then every page view — each
 * client-side route change included — is a session and a user of its own:
 * the project showed 86 sessions, 86 users, 1.01 pages a session. Tag
 * Manager fires the tag only under `analytics_storage`, so the signal is
 * sent only then, onto the same queue the tag's snippet creates
 * (`window.clarity.q`), which Clarity drains when it loads.
 */
function pushClarityConsent(state: ConsentState): void {
  const w = window as typeof window & { clarity?: ((...args: unknown[]) => void) & { q?: unknown[] } };
  w.clarity =
    w.clarity ||
    function (...args: unknown[]) {
      (w.clarity!.q = w.clarity!.q || []).push(args);
    };
  w.clarity("consentv2", {
    analytics_Storage: "granted",
    ad_Storage: state.marketing === "accepted" ? "granted" : "denied",
  });
}

let gtmLoaded = false;
const trackingListeners = new Set<() => void>();

/**
 * Whether events may go to `dataLayer` at all. True once the container has
 * been asked for, which only happens after a category was accepted — so an
 * event from a visitor who has not chosen, or who declined both, is dropped
 * rather than queued for a container that would replay it after a later yes.
 */
export const isTracking = (): boolean => gtmLoaded;

/**
 * Called once, when tracking starts partway through a page view — the visitor
 * accepted in the banner. The page they are on was never reported, and this is
 * how `src/lib/analytics.ts` reports it.
 */
export function onTrackingStart(listener: () => void): () => void {
  trackingListeners.add(listener);
  return () => trackingListeners.delete(listener);
}

/** Inserts the GTM script. Guarded so a second "accept" is a no-op, and so the
 *  dev server and the build-time prerender — both on localhost — never fire it. */
function loadGTM(): void {
  if (gtmLoaded) return;
  if (/^(localhost|127\.0\.0\.1|\[::1\])$/.test(location.hostname)) return;
  gtmLoaded = true;

  const w = window as typeof window & { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);

  for (const listener of trackingListeners) listener();
}

// The consent signal has to be on `dataLayer` before the container loads, so
// a tag checking it on its very first evaluation still sees the right answer.
pushConsentSignal(consent);
if (consent && (consent.analytics === "accepted" || consent.marketing === "accepted")) loadGTM();
